/**
 * The four web sections, in the order a fan learns them. Changing this
 * order moves an item they already know. It also holds the browser tab title
 * for every signed-in screen (ISSUE-1186).
 */
import { useEffect } from "react";

import { ACCOUNT_HEADING } from "../features/accountHome/accountHomeCopy";
import { EDIT_PROFILE_HEADING } from "../features/editProfile/editProfileLogic";
import { FAN_PROFILE_HEADING } from "../features/fanProfile/fanProfileCopy";
import { FOLLOWERS_HEADING } from "../features/followers/followCopy";
import { FOLLOWS_HEADING } from "../features/follows/followsEditorModel";
import { BLOCKED_HEADING } from "../features/privacy/privacyScreenLogic";
import { SESSIONS_HEADING } from "../features/sessions/sessionsScreenLogic";
import { SIGN_IN_METHODS_HEADING } from "../features/signInMethods/signInMethodsScreenLogic";

export const WEB_SECTIONS = [
  {
    key: "scores",
    to: "/scores",
    label: "Scores",
    title: "Scores · Sportsmet",
  },
  { key: "pools", to: "/pools", label: "Pools", title: "Pools · Sportsmet" },
  // Invite promotes the app; received invitations live under Pools.
  {
    key: "invites",
    to: "/refer",
    label: "Invite",
    title: "Refer a friend · Sportsmet",
  },
  {
    key: "messages",
    to: "/messages",
    label: "Messages",
    title: "Messages · Sportsmet",
  },
] as const;

export type typeWebSection = (typeof WEB_SECTIONS)[number];
export type typeWebSectionKey = typeWebSection["key"];

export const DEFAULT_DOCUMENT_TITLE = "Sportsmet";

/**
 * Signed-in screens outside the four sections, keyed by exact browser path.
 * Each title is the heading that screen renders, then " · Sportsmet"
 * (ISSUE-1186). A screen with no row here falls back to the section prefix
 * match, then to DEFAULT_DOCUMENT_TITLE. /get-started drops the brand from
 * its "Welcome to Sportsmet" heading so the tab does not name it twice.
 */
export const SCREEN_DOCUMENT_TITLES: Readonly<Record<string, string>> = {
  "/get-started": "Welcome · Sportsmet",
  "/pools/invitations": "Pool invitations · Sportsmet",
  "/notifications": "Notifications · Sportsmet",
  "/account": `${ACCOUNT_HEADING} · Sportsmet`,
  "/account/edit": `${EDIT_PROFILE_HEADING} · Sportsmet`,
  "/account/settings/notifications": "Notification settings · Sportsmet",
  "/account/followers": `${FOLLOWERS_HEADING} · Sportsmet`,
  "/account/follows": `${FOLLOWS_HEADING} · Sportsmet`,
  "/fan": `${FAN_PROFILE_HEADING} · Sportsmet`,
  "/settings": "Settings · Sportsmet",
  "/settings/sign-in-methods": `${SIGN_IN_METHODS_HEADING} · Sportsmet`,
  "/settings/sessions": `${SESSIONS_HEADING} · Sportsmet`,
  "/settings/blocked": `${BLOCKED_HEADING} · Sportsmet`,
};

export function resolveActiveSection(
  pathname: string,
): typeWebSection | undefined {
  return WEB_SECTIONS.find(
    (section) =>
      pathname === section.to || pathname.startsWith(`${section.to}/`),
  );
}

export function resolveDocumentTitle(pathname: string): string {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  if (SCREEN_DOCUMENT_TITLES[normalized] !== undefined) {
    return SCREEN_DOCUMENT_TITLES[normalized];
  }
  return resolveActiveSection(pathname)?.title ?? DEFAULT_DOCUMENT_TITLE;
}

export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = title;
  }, [title]);
}
